import React, { useEffect } from "react";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { useSearchParams } from "@/hooks/useSearchParams";
import {
  type DepositModalParams,
  depositModalParams,
  hasDepositReturnParams,
} from "@/features/wallet/components/deposit/models/depositModalParams";
import { useInteracDepositReturn } from "../../hooks/useInteracDepositReturn";
import { DepositModal } from "./DepositModal";

/**
 * The app-wide deposit modal, kept in sync with the url so offsite payments
 * can return straight into it
 */
const DepositModalContainer = () => {
  const modal = useShellStore((state) => state.modal);
  const setModal = useShellStore((state) => state.setModal);

  const { searchParams, setSearchParams } = useSearchParams(depositModalParams);
  const { depositModal } = searchParams;

  const isOpen = modal === "deposit";
  const isReturning = hasDepositReturnParams(searchParams);

  // an interac return lands on a backend chosen url and is about to be bounced
  // to the page the user left, so neither the modal nor the url may settle yet
  const isRestoringInteracReturn = useInteracDepositReturn();

  // the url only signals inbound. it opens the modal and is consumed on the way
  // in, leaving the layout store as the single owner of the open state. the
  // return params are left in place for the wizard, which captures them itself
  useEffect(() => {
    if (isRestoringInteracReturn) {
      return;
    }

    if (!depositModal && !isReturning) {
      return;
    }

    setModal("deposit");

    if (depositModal) {
      setSearchParams({ depositModal: undefined }, { replace: true });
    }
  }, [depositModal, isReturning, isRestoringInteracReturn]);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      return;
    }

    setSearchParams(
      {
        transactionId: undefined,
        return: undefined,
        cancel: undefined,
        method: undefined,
        depositModal: undefined,
      } satisfies DepositModalParams,
      { replace: true },
    );
    setModal(null);
  };

  return <DepositModal isOpen={isOpen} onOpenChange={handleOpenChange} />;
};

export { DepositModalContainer };
